import { Col, Layout, Row, Skeleton, Space, Typography, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { PropsWithChildren } from 'react';
import BreadCrumbs from '../../../components/BreadCrumbs';
import ProfilePicture from '../../../components/ProfilePicture';
import UserActionButton from '../../../components/UsersPage/UserActionButton';
import TicketInfoLeftSideTabs from '../../../containers/Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/TicketInfoLeftSideTabs';
import useTicketByKey from '../../../hooks/ticketDetailPage/useTicketByKey';
import useUserById, { deriveCustomizedDetailedUser } from '../../../hooks/useUserById';
import { DetailedUser, TicketSystemFieldsKey } from '../../../model/types';
import { TAB_BAR_HEIGHT, TICKET_SIDEBAR_WIDTH } from '../../../styles/theme';
import { isCustomerPage } from '../../../utils/routeHelper';
import { BreadCrumbsViewState } from './TicketDetailContentLayout';

const { Content, Sider } = Layout;

const styles = { fullName: { marginTop: '0.5rem' } };

export enum TicketSelectionState {
    assigned = 'assigned',
    requested = 'requested',
    followed = 'followed',
    cc = 'cc'
}

// to easily get requester id in users/[userId]/... and tickets/[ticketKey]/requester/... pages
export function useRequesterId() {
    const {
        query: { ticketKey, userId }
    } = useRouter();
    const { data: ticketDetail } = useTicketByKey(ticketKey?.toString());
    const requesterId =
        userId?.toString() ?? (ticketDetail?.fieldMap?.[TicketSystemFieldsKey.requester]?.value as DetailedUser)?.id;

    return requesterId;
}
function HeaderSection() {
    const { asPath } = useRouter();
    const requesterId = useRequesterId();
    const { data: userInfo } = useUserById(requesterId?.toString());
    const requesterInfo = deriveCustomizedDetailedUser(userInfo);
    if (!requesterInfo) return <Skeleton avatar={{ size: 45 }} paragraph={{ rows: 1 }} />;

    const isUserTabRelatedPage = isCustomerPage(asPath);
    const titleText =
        requesterInfo?.fullName ?? requesterInfo?.primaryEmail?.email ?? requesterInfo?.primaryPhone?.phone;

    return (
        <Row justify="space-between" align="middle" className="full-width">
            <Col span={20}>
                <Space>
                    <ProfilePicture size="large" src={userInfo?.profilePicture} />
                    <Typography.Title level={4} style={styles.fullName}>
                        {titleText}
                    </Typography.Title>
                </Space>
            </Col>

            {/* // we want to expose UserActionButton component to user for the pages which its url only if starts with '/users', due to limitation related with techical debt */}
            {isUserTabRelatedPage && (
                <Col span={1}>
                    <UserActionButton user={requesterInfo} />
                </Col>
            )}
            <Col span={1} />
        </Row>
    );
}

const RequesterDetailContentLayout = ({ children }: PropsWithChildren) => {
    const {
        token: { colorBgLayout, colorSplit, lineWidth, paddingMD, paddingSM }
    } = theme.useToken();

    return (
        <Layout className="full-height" hasSider>
            <Sider
                width={TICKET_SIDEBAR_WIDTH}
                theme="light"
                style={{
                    padding: paddingSM,
                    backgroundColor: colorBgLayout,
                    borderInlineEnd: `${lineWidth}px solid ${colorSplit}`,
                    height: `calc(100vh - ${TAB_BAR_HEIGHT}px)`,
                    overflowY: 'auto'
                }}
            >
                <BreadCrumbs viewOption={BreadCrumbsViewState.userView} />
                <TicketInfoLeftSideTabs viewOption={BreadCrumbsViewState.userView} />
            </Sider>

            <Layout className="full-height">
                <Row
                    align="middle"
                    justify="space-between"
                    style={{
                        paddingBottom: paddingSM,
                        paddingLeft: paddingSM,
                        paddingRight: 0,
                        paddingTop: paddingMD,
                        borderBottom: `${lineWidth}px solid ${colorSplit}`
                    }}
                >
                    <HeaderSection />
                </Row>
                <Content style={{ overflow: 'auto', padding: paddingSM, paddingTop: 0 }}>{children}</Content>
            </Layout>
        </Layout>
    );
};

export default observer(RequesterDetailContentLayout);
